import { Controller } from "@hotwired/stimulus"

interface SavedUpload {
  ok: boolean
  name?: string
  path?: string
  size?: string
  saved_at?: string
  error?: string
}

// /dev 개발 도구 — 클립보드 붙여넣기, 파일 올리기, 저장 경로 복사.
//
// 캡처를 올린 뒤 경로를 바로 붙여넣어 쓸 수 있도록, 저장되면 서버 절대경로를
// 자동으로 복사하고 화면에도 남긴다. 목록은 새로고침 없이 갱신한다
// (새로고침하면 방금 복사한 경로 안내가 사라져 버린다).
export default class extends Controller<HTMLElement> {
  static targets = [
    "name", "extension", "content", "status", "file", "dropzone",
    "result", "resultPath", "list", "empty", "count"
  ]
  static values = { clipboardUrl: String, fileUrl: String }

  declare readonly nameTarget: HTMLInputElement
  declare readonly extensionTarget: HTMLInputElement
  declare readonly contentTarget: HTMLTextAreaElement
  declare readonly statusTarget: HTMLElement
  declare readonly fileTarget: HTMLInputElement
  declare readonly dropzoneTarget: HTMLElement
  declare readonly resultTarget: HTMLElement
  declare readonly resultPathTarget: HTMLElement
  declare readonly listTarget: HTMLElement
  declare readonly emptyTarget: HTMLElement
  declare readonly countTarget: HTMLElement
  declare readonly clipboardUrlValue: string
  declare readonly fileUrlValue: string

  // 붙여넣기: 캡처 이미지가 들어 있으면 파일로 올린다. 텍스트는 기본 동작에 맡긴다.
  paste(event: ClipboardEvent): void {
    const items = Array.from(event.clipboardData?.items ?? [])
    const image = items.find((item) => item.type.startsWith("image/"))
    if (!image) return

    const file = image.getAsFile()
    if (!file) return

    event.preventDefault()
    // 클립보드 이미지는 이름이 없거나 image.png 로 온다. 알아보기 쉽게 바꿔 준다.
    const named = new File([ file ], this.captureName(file), { type: file.type })
    void this.upload([ named ])
  }

  saveText(): void {
    const content = this.contentTarget.value
    if (!content.trim()) {
      this.report("붙여넣을 내용이 없습니다.", false)
      return
    }

    void this.post(this.clipboardUrlValue, this.formOf({
      content,
      name: this.nameTarget.value,
      extension: this.extensionTarget.value
    }))
  }

  pick(): void {
    void this.upload(Array.from(this.fileTarget.files ?? []))
  }

  dragOver(event: DragEvent): void {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-[#2A6E4B]", "bg-[#F2F8F5]")
  }

  dragLeave(): void {
    this.dropzoneTarget.classList.remove("border-[#2A6E4B]", "bg-[#F2F8F5]")
  }

  drop(event: DragEvent): void {
    event.preventDefault()
    this.dragLeave()
    void this.upload(Array.from(event.dataTransfer?.files ?? []))
  }

  // 목록의 복사 버튼
  copyPath(event: Event): void {
    const button = event.currentTarget as HTMLElement
    void this.copy(button.dataset.path ?? "", button)
  }

  // 방금 올린 것의 복사 버튼
  copyResult(event: Event): void {
    void this.copy(this.resultPathTarget.textContent ?? "", event.currentTarget as HTMLElement)
  }

  // ---------------------------------------------------------------- 내부

  private async upload(files: File[]): Promise<void> {
    if (files.length === 0) return

    let last: SavedUpload | null = null
    for (const [ index, file ] of files.entries()) {
      this.report(`올리는 중… (${index + 1}/${files.length}) ${file.name}`, null)
      const form = new FormData()
      form.append("file", file, file.name)
      last = await this.post(this.fileUrlValue, form, { quiet: true })
      if (!last?.ok) return
    }

    if (last?.ok) this.announce(last, files.length)
  }

  private formOf(fields: Record<string, string>): FormData {
    const form = new FormData()
    for (const [ key, value ] of Object.entries(fields)) form.append(key, value)
    return form
  }

  private async post(url: string, form: FormData, options: { quiet?: boolean } = {}): Promise<SavedUpload | null> {
    try {
      const response = await fetch(url, {
        method: "POST",
        body: form,
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" }
      })
      const data = (await response.json()) as SavedUpload

      if (!data.ok) {
        this.report(data.error ?? "저장에 실패했습니다.", false)
        return data
      }
      if (!options.quiet) this.announce(data, 1)
      return data
    } catch (error) {
      this.report(`저장에 실패했습니다: ${String(error)}`, false)
      return null
    }
  }

  // 저장 결과를 화면에 남기고 경로를 자동으로 복사한다.
  private async announce(upload: SavedUpload, count: number): Promise<void> {
    const path = upload.path ?? ""
    this.resultPathTarget.textContent = path
    this.resultTarget.hidden = false
    this.prependRow(upload)
    this.contentTarget.value = ""

    const copied = await this.writeClipboard(path)
    const prefix = count > 1 ? `${count}개 저장 완료. 마지막 경로를 ` : "저장 완료. 경로를 "
    this.report(copied ? `${prefix}복사했습니다.` : `${prefix}복사하지 못했습니다. 복사 버튼을 눌러 주세요.`, copied)
  }

  private prependRow(upload: SavedUpload): void {
    const row = document.createElement("tr")
    row.className = "bg-emerald-50/60"
    row.append(
      this.cell("px-4 py-2", this.code(upload.path ?? "")),
      this.cell("px-4 py-2 text-right tabular-nums", document.createTextNode(upload.size ?? "")),
      this.cell("px-4 py-2 text-center text-xs text-slate-500", document.createTextNode(upload.saved_at ?? "")),
      this.cell("px-4 py-2 text-right", this.copyButton(upload.path ?? ""))
    )
    this.listTarget.prepend(row)
    this.emptyTarget.hidden = true
    this.countTarget.textContent = `(${this.listTarget.querySelectorAll("tr").length})`
  }

  private cell(className: string, child: Node): HTMLTableCellElement {
    const td = document.createElement("td")
    td.className = className
    td.append(child)
    return td
  }

  private code(text: string): HTMLElement {
    const code = document.createElement("code")
    code.className = "block break-all font-mono text-xs text-slate-800"
    code.textContent = text
    return code
  }

  private copyButton(path: string): HTMLButtonElement {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50"
    button.textContent = "복사"
    button.dataset.path = path
    button.dataset.action = "clipboard-upload#copyPath"
    return button
  }

  private async copy(text: string, button: HTMLElement): Promise<void> {
    if (!text) return

    const ok = await this.writeClipboard(text)
    const original = button.textContent
    button.textContent = ok ? "복사됨" : "실패"
    window.setTimeout(() => (button.textContent = original), 1200)
    if (!ok) this.report("복사하지 못했습니다. 경로를 직접 선택해 복사해 주세요.", false)
  }

  // navigator.clipboard 는 HTTPS(또는 localhost)에서만 쓸 수 있다.
  // 막힌 환경에서는 숨은 textarea + execCommand 로 떨어진다.
  private async writeClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      return this.legacyCopy(text)
    }
  }

  private legacyCopy(text: string): boolean {
    const area = document.createElement("textarea")
    area.value = text
    area.setAttribute("readonly", "")
    area.style.position = "fixed"
    area.style.opacity = "0"
    document.body.append(area)
    area.select()
    let ok = false
    try {
      ok = document.execCommand("copy")
    } catch {
      ok = false
    }
    area.remove()
    return ok
  }

  // 클립보드 캡처에 쓸 이름: 캡처_20260824_192632.png
  private captureName(file: File): string {
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, "0")
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
      `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
    const ext = file.type.split("/")[1] ?? "png"
    const base = this.nameTarget.value.trim() || "캡처"
    return `${base}_${stamp}.${ext}`
  }

  private csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
  }

  private report(message: string, ok: boolean | null): void {
    this.statusTarget.textContent = message
    this.statusTarget.className = "text-xs " +
      (ok === null ? "text-slate-500" : ok ? "text-emerald-600" : "text-rose-600")
  }
}
